// CheckYours — paste an ingredient list, a product name or an Amazon link, or photograph a label;
// the result opens in the IngredientSheet. A name or link is looked up online on the server
// (BloomResearchService), so the busy line moves on while that runs.
import React, { useEffect, useRef, useState } from "react";
import type { Data } from "./IngredientSheet";
import { fill, useLang } from "./i18n";
import { useMessages } from "./messages";
import { signal } from "./pulse";

const API_BASE = "/api/bloom";
// Long edge of the uploaded photo. Enough for small label print, small enough to post quickly.
const MAX_EDGE = 1600;

function shrinkPhoto(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            try {
                const scale = Math.min(1, MAX_EDGE / Math.max(img.naturalWidth, img.naturalHeight));
                const w = Math.max(1, Math.round(img.naturalWidth * scale));
                const h = Math.max(1, Math.round(img.naturalHeight * scale));
                const canvas = document.createElement("canvas");
                canvas.width = w;
                canvas.height = h;
                const ctx = canvas.getContext("2d");
                if (!ctx) throw new Error("no canvas");
                ctx.drawImage(img, 0, 0, w, h);
                resolve(canvas.toDataURL("image/jpeg", 0.82));
            } catch (e) {
                reject(e);
            } finally {
                URL.revokeObjectURL(url);
            }
        };
        img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("bad image")); };
        img.src = url;
    });
}

function CameraIcon({ className }: { className?: string }) {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
            <path d="M4 8h3l2-3h6l2 3h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1Z" />
            <circle cx="12" cy="13" r="3.5" />
        </svg>
    );
}

export default function CheckYours({ onClose, onResult, start }: { onClose: () => void; onResult: (d: Data) => void; start?: "text" | "photo" }) {
    const { t, lang } = useLang();
    const m = useMessages();
    const [tab, setTab] = useState<"text" | "photo">(start || "text");
    const [phase, setPhase] = useState(0);
    const [offerPhoto, setOfferPhoto] = useState(false);
    const [text, setText] = useState("");
    const [name, setName] = useState("");
    const [photo, setPhoto] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const fileRef = useRef<HTMLInputElement>(null);
    const areaRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        signal("check_open");
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = prev;
            window.removeEventListener("keydown", onKey);
        };
    }, [onClose]);

    useEffect(() => {
        if (tab === "text") areaRef.current?.focus();
    }, [tab]);

    // Reading, then looking the list up online, then explaining it: the server does not report
    // progress, so the line moves on with time.
    useEffect(() => {
        if (!busy) { setPhase(0); return; }
        const a = window.setTimeout(() => setPhase(1), 3500);
        const b = window.setTimeout(() => setPhase(2), 16000);
        return () => { window.clearTimeout(a); window.clearTimeout(b); };
    }, [busy]);

    const pick = async (f: File | undefined) => {
        if (!f) return;
        setError(null);
        try {
            setPhoto(await shrinkPhoto(f));
        } catch (e) {
            setError(t.checkErrors.bad_image);
        }
    };

    const ready = tab === "text" ? text.trim().length >= 8 : !!photo;

    const submit = async () => {
        if (!ready || busy) return;
        setBusy(true);
        setError(null);
        setOfferPhoto(false);
        try {
            const body = tab === "text" ? { text, name, lang } : { image: photo, name, lang };
            const res = await fetch(`${API_BASE}/check`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Accept: "application/json" },
                body: JSON.stringify(body),
            });
            const ctype = res.headers.get("content-type") || "";
            if (!ctype.includes("application/json")) throw new Error("generic");
            const d = await res.json();
            if (d.error === "not_found_online") {
                // B21: an Amazon link or ASIN says why nothing was found; every answer offers a photo of the panel.
                const msg = d.reason === "not_on_amazon" && d.asin ? fill(m.notFoundAmazon, { asin: d.asin })
                    : d.reason === "amazon_paused" ? m.amazonPaused
                    : fill(m.notFoundOnline, { product: d.product || text.trim().slice(0, 80) });
                setError(msg);
                setOfferPhoto(true);
                return;
            }
            if (d.error === "lookup_unavailable") {
                setError(m.lookupUnavailable);
                return;
            }
            if (!res.ok || !d.ok) throw new Error(d.error || "generic");
            signal("check_run", { c: d.checkId });
            onResult(d as Data);
        } catch (e) {
            const code = e instanceof Error ? e.message : "generic";
            setError(t.checkErrors[code] || t.checkErrors.generic);
        } finally {
            setBusy(false);
        }
    };

    const tabBtn = (id: "text" | "photo", label: string) => (
        <button
            type="button"
            role="tab"
            aria-selected={tab === id}
            onClick={() => { setTab(id); setError(null); }}
            className={
                "flex-1 rounded-full px-3 py-1.5 text-sm font-semibold transition " +
                (tab === id ? "bg-white text-gray-900 shadow" : "text-gray-500 hover:text-gray-800")
            }
        >
            {label}
        </button>
    );

    return (
        <div className="fixed inset-0 z-[60]" role="dialog" aria-modal="true" aria-label={t.checkTitle}>
            <div className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm animate-in fade-in duration-200" onClick={onClose} />
            <div
                className={
                    "absolute inset-x-0 bottom-0 flex max-h-[92vh] flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl " +
                    "animate-in max-md:slide-in-from-bottom duration-300 " +
                    "md:inset-auto md:left-1/2 md:top-1/2 md:w-[520px] md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-3xl md:zoom-in-95"
                }
            >
                <div className="mx-auto mt-2 h-1.5 w-10 rounded-full bg-gray-200 md:hidden" />
                <div className="relative bg-gradient-to-br from-green-50 via-white to-emerald-50 px-5 pb-4 pt-4">
                    <button
                        onClick={onClose}
                        aria-label={t.close}
                        className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-white text-xl text-gray-500 shadow ring-1 ring-gray-100 transition hover:rotate-90 hover:text-gray-900"
                    >
                        &times;
                    </button>
                    <h2 className="pr-10 text-lg font-extrabold text-gray-900">{t.checkTitle}</h2>
                    <p className="mt-1 pr-6 text-sm text-gray-600">{m.checkHint2}</p>
                    <div role="tablist" className="mt-4 flex gap-1 rounded-full bg-gray-100 p-1">
                        {tabBtn("text", t.pasteTab)}
                        {tabBtn("photo", t.photoTab)}
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto px-5 pb-4">
                    {tab === "text" ? (
                        <textarea
                            ref={areaRef}
                            value={text}
                            onChange={(e) => setText(e.target.value.slice(0, 4000))}
                            rows={7}
                            placeholder={m.pasteHolder2}
                            className="mt-3 w-full resize-none rounded-2xl border border-gray-200 bg-gray-50 p-3 text-sm leading-relaxed text-gray-800 focus:border-green-500 focus:bg-white focus:outline-none"
                        />
                    ) : (
                        <div className="mt-3">
                            <input
                                ref={fileRef}
                                type="file"
                                accept="image/*"
                                capture="environment"
                                className="hidden"
                                onChange={(e) => pick(e.target.files?.[0])}
                            />
                            {photo ? (
                                <div className="relative overflow-hidden rounded-2xl ring-1 ring-gray-200">
                                    <img src={photo} alt="" className="max-h-64 w-full object-contain bg-gray-50" />
                                    <button
                                        type="button"
                                        onClick={() => fileRef.current?.click()}
                                        className="absolute bottom-2 right-2 rounded-full bg-white/95 px-3 py-1.5 text-xs font-semibold text-gray-800 shadow"
                                    >
                                        {t.retakePhoto}
                                    </button>
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => fileRef.current?.click()}
                                    className="flex h-44 w-full flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-green-300 bg-green-50/60 text-green-800 transition hover:bg-green-50"
                                >
                                    <CameraIcon className="h-9 w-9" />
                                    <span className="text-sm font-semibold">{t.takePhoto}</span>
                                </button>
                            )}
                        </div>
                    )}
                    <input
                        value={name}
                        onChange={(e) => setName(e.target.value.slice(0, 80))}
                        placeholder={t.nameOptional}
                        className="mt-3 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-800 focus:border-green-500 focus:outline-none"
                    />
                    {error ? (
                        <div className="mt-3 rounded-xl bg-rose-50 p-3 text-sm text-rose-700 ring-1 ring-rose-100">
                            <p>{error}</p>
                            {offerPhoto ? (
                                <button
                                    type="button"
                                    onClick={() => { setTab("photo"); setError(null); setOfferPhoto(false); }}
                                    className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-xs font-bold text-green-800 shadow-sm ring-1 ring-green-200"
                                >
                                    <CameraIcon className="h-4 w-4" />{m.photoInstead}
                                </button>
                            ) : null}
                        </div>
                    ) : null}
                    <p className="mt-3 text-[11px] leading-relaxed text-gray-400">{t.privacyNote} {t.aiNote}</p>
                </div>

                <div className="border-t border-gray-100 bg-white p-4">
                    <button
                        type="button"
                        onClick={submit}
                        disabled={!ready || busy}
                        className="relative block w-full overflow-hidden rounded-xl bg-green-600 py-3 text-center text-sm font-bold text-white shadow-lg shadow-green-600/20 transition hover:bg-green-700 active:scale-[.99] disabled:opacity-50"
                    >
                        {busy ? (
                            <span className="inline-flex items-center gap-2">
                                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                                {phase === 0 ? t.analysing : phase === 1 && tab === "text" ? m.busyLookup : m.busyExplain}
                            </span>
                        ) : (
                            t.analyse
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}
